import type { Address } from '@paid-ui/schemas/zod/address';

import { forwardRef, useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { ErrorBoundary } from 'react-error-boundary';
import { twMerge } from 'tailwind-merge';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import { useSnapshot } from 'valtio/react';

import { CountryCode } from '@paid-ui/enums/address';
import { CheckIcon } from '@paid-ui/icons/check';
import { ChevronsUpDownIcon } from '@paid-ui/icons/chevrons-up-down';
import { MapPinIcon } from '@paid-ui/icons/map-pin';
import { appConfigManager } from '@paid-ui/models/app-config';
import { useToast } from '@paid-ui/ui/toast';

import { AddressInput } from '../address-input';
import { GoogleMapsMarker } from '../google-maps';
import { formatAddress, formatGoogleMapsComponents } from './utils';

const iconVariants = cva(
  'pointer-events-none absolute inset-y-0 inline-flex h-full shrink-0 select-none items-center justify-center text-base text-grey-dark empty:hidden',
  {
    variants: {
      disabled: {
        true: 'text-grey-dark',
      },
      position: {
        prefix: 'left-3.5',
        suffix: 'right-3.5',
      },
    },
  },
);

const buttonVariants = cva(
  'relative h-[54px] w-full rounded border border-grey-tertiary bg-white pl-3.5 pr-11 text-left text-black outline-none transition-colors duration-150 ease-linear focus-within:border-blue focus-within:shadow-field disabled:cursor-not-allowed disabled:bg-grey-hover disabled:text-grey-dark',
  {
    variants: {
      changed: {
        true: 'border-purple',
      },
      error: {
        true: 'border-pink focus-within:border-pink focus-within:shadow-field-error',
      },
      compact: {
        true: 'h-[44px]',
      },
      prefix: {
        true: 'pl-11',
      },
    },
  },
);

const optionsVariants = cva(
  'absolute inset-x-0 z-[500] mt-[22px] min-w-[250px] overflow-y-auto rounded-md border border-grey-mid bg-white py-1.5 shadow-md',
  {
    variants: {
      compact: {
        true: 'mt-[17px]',
      },
    },
  },
);

const optionVariants = cva(
  'relative cursor-pointer border-b border-grey-mid py-2.5 pl-4 pr-12 text-base transition-colors duration-150 ease-linear last:border-none',
  {
    variants: {
      active: {
        true: 'bg-grey-light',
      },
      selected: {
        true: '',
      },
      disabled: {
        true: 'cursor-not-allowed bg-transparent text-grey-mid',
      },
      manual: {
        true: 'font-semibold',
      },
    },
  },
);

const checkVariants = cva('absolute inset-y-0 right-4 hidden items-center justify-center', {
  variants: {
    selected: {
      true: 'inline-flex',
    },
    disabled: {
      true: 'text-grey-mid',
    },
  },
});

export interface PlaceAutocompleteProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'prefix'> {
  value?: Address | '';
  changed?: boolean;
  error?: boolean;
  compact?: boolean;
  prefix?: React.ReactNode;
  maxHeight?: string | number;
  searchMode?: 'includes' | 'startsWith';
  selectedOnFocus?: boolean;
  enablePreview?: boolean;
  region?: CountryCode;
  language?: string;
  mapId?: string;
  onValueChange?: (value: Address | '') => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onEnter?: React.KeyboardEventHandler<HTMLInputElement>;
}

const errorStatusMap = {
  'OK': 'The response contains a valid result.',
  'NOT_FOUND': 'The place referenced was not found.',
  'ZERO_RESULTS': 'No result was found for this request.',
  'INVALID_REQUEST': 'This request was invalid.',
  'OVER_QUERY_LIMIT': 'The app has gone over its request quota.',
  'REQUEST_DENIED': 'The app is not allowed to use the PlacesService.',
  'UNKNOWN_ERROR': 'Unknown error',
  '': 'Unknown error',
} as const;

const BasePlaceAutocomplete = forwardRef<
  HTMLInputElement,
  PlaceAutocompleteProps & { onManualChange: (value: boolean) => void }
>(
  (
    {
      name,
      type,
      value,
      placeholder = 'Enter keyword to search',
      readOnly,
      disabled,
      changed,
      error,
      compact,
      prefix = <MapPinIcon className="h-[1em] w-[1em] text-grey-dark" />,

      // item height * number of item + padding y
      maxHeight = Math.max(42 * 8 + 10 + 12, 54),
      searchMode = 'includes',
      selectedOnFocus = true,

      enablePreview = true,
      region = CountryCode.AU,
      language = 'en-AU',
      mapId,

      hidden,
      className,
      children,

      onChange,
      onFocus,
      onBlur,
      onEnter,
      onValueChange,
      onManualChange,

      ...restProps
    },
    ref,
  ) => {
    const toast = useToast();
    const valueAsStr = formatAddress(value);

    const {
      ready,
      value: inputValue,
      setValue,
      suggestions,
    } = usePlacesAutocomplete({
      debounce: 300,
      defaultValue: valueAsStr,
      callbackName: 'initMap',
      requestOptions: {
        language,
        componentRestrictions: {
          country: [region],
        },
      },
    });

    const { status, loading, data: options } = suggestions;

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      const newValue = event.currentTarget.value;
      setValue(newValue);
    };

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => {
      if (selectedOnFocus) event.currentTarget.select();
      onFocus?.(event);
    };

    const handleValueChange = async (address: string) => {
      if (address === 'MANUAL') {
        onManualChange(true);
        return;
      }

      try {
        const [decodedAddress] = await getGeocode({ address, language, region });

        if (!decodedAddress) {
          setValue('');
          onValueChange?.('');
          return;
        }

        const components = decodedAddress.address_components;
        const newAddress = formatGoogleMapsComponents(components);

        onValueChange?.(newAddress);
        setValue(newAddress ? (decodedAddress?.formatted_address ?? '') : '');
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Fail to decode address');
      }
    };

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setValue(valueAsStr);

      // Workaround to prevent onBlur from firing when clicking on the option
      if (!valueAsStr && options.length > 0) return;

      onBlur?.(event);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
      if (event.key === 'Enter') {
        onEnter?.(event);
      }
    };

    useEffect(() => {
      if (!globalThis.google) {
        toast.error('Google Maps API is not loaded');
        return;
      }

      setValue(valueAsStr);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    if (hidden) {
      return null;
    }

    return (
      <div className="w-full space-y-2">
        <Combobox
          name={name}
          value={valueAsStr}
          multiple={false}
          disabled={!ready || disabled}
          onChange={handleValueChange}
        >
          <Combobox.Button
            type="button"
            as="button"
            data-name={name}
            className={twMerge(
              buttonVariants({
                changed,
                error,
                compact,
                prefix: Boolean(prefix),
                className,
              }),
            )}
          >
            <div
              className={iconVariants({
                disabled,
                position: 'prefix',
              })}
            >
              {prefix}
            </div>
            <Combobox.Input
              as="input"
              ref={ref}
              type={type}
              name={name}
              value={inputValue}
              placeholder={placeholder}
              readOnly={readOnly}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              className={twMerge(
                'w-full placeholder:text-grey-dark disabled:cursor-not-allowed disabled:bg-grey-hover disabled:text-grey-dark disabled:placeholder:text-grey-tertiary',
              )}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              {...restProps}
            />
            <div
              className={iconVariants({
                disabled,
                position: 'suffix',
              })}
            >
              <ChevronsUpDownIcon />
            </div>
            {['OK', 'NOT_FOUND', 'ZERO_RESULTS', ''].includes(status) ? (
              <Combobox.Options
                style={{ maxHeight }}
                hidden={readOnly || disabled}
                className={twMerge(optionsVariants({ compact }))}
              >
                {options.map((option) => (
                  <Combobox.Option
                    key={option.place_id}
                    value={option.description}
                    className={({ active, disabled, selected }) =>
                      twMerge(
                        optionVariants({
                          active,
                          disabled,
                          selected,
                        }),
                      )
                    }
                  >
                    {({ disabled, selected }) => (
                      <>
                        {option.description}
                        <span
                          className={twMerge(
                            checkVariants({
                              selected,
                              disabled,
                            }),
                          )}
                        >
                          <CheckIcon className="h-4 w-4 text-blue" />
                        </span>
                      </>
                    )}
                  </Combobox.Option>
                ))}
                <Combobox.Option
                  key="MANUAL"
                  value="MANUAL"
                  className={({ active, disabled, selected }) =>
                    twMerge(
                      optionVariants({
                        active,
                        disabled,
                        selected,
                        manual: true,
                      }),
                    )
                  }
                >
                  {({ disabled, selected }) => (
                    <>
                      Use address as entered
                      <span
                        className={twMerge(
                          checkVariants({
                            selected,
                            disabled,
                          }),
                        )}
                      >
                        <CheckIcon className="h-4 w-4 text-blue" />
                      </span>
                    </>
                  )}
                </Combobox.Option>
              </Combobox.Options>
            ) : (
              <Combobox.Options
                style={{ maxHeight }}
                hidden={readOnly || disabled}
                className={twMerge(optionsVariants({ compact }))}
              >
                <div className="px-3.5 py-2.5 text-grey-dark">
                  {loading ? 'Loading suggestions...' : errorStatusMap[status]}
                </div>
              </Combobox.Options>
            )}
          </Combobox.Button>
        </Combobox>
        {enablePreview && valueAsStr ? (
          <GoogleMapsMarker value={value} region={region} language={language} mapId={mapId} />
        ) : null}
      </div>
    );
  },
);

export const PlaceAutocomplete = forwardRef<HTMLInputElement, PlaceAutocompleteProps>(
  (
    {
      name,
      value,
      disabled,
      readOnly,
      error,
      changed,
      hidden,
      id,
      style,
      className,
      onBlur,
      onFocus,
      onValueChange,
      ...restProps
    },
    ref,
  ) => {
    const [manual, setManual] = useState(false);
    const { mapId } = useSnapshot(appConfigManager);

    if (hidden) {
      return null;
    }

    if (manual) {
      return (
        <div>
          <AddressInput
            id={id}
            style={style}
            className={className}
            name={name}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            error={error}
            changed={changed}
            onFocus={onFocus}
            onBlur={onBlur}
            onValueChange={onValueChange}
            onManualChange={setManual}
          />
        </div>
      );
    }

    return (
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <div id={id} style={style} className={className}>
            <AddressInput
              id={id}
              style={style}
              className={className}
              name={name}
              value={value}
              disabled={disabled}
              readOnly={readOnly}
              error={error}
              changed={changed}
              onFocus={onFocus}
              onBlur={onBlur}
              onValueChange={onValueChange}
            />
          </div>
        )}
      >
        <BasePlaceAutocomplete
          ref={ref}
          id={id}
          mapId={mapId}
          style={style}
          className={className}
          name={name}
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          error={error}
          changed={changed}
          onBlur={onBlur}
          onFocus={onFocus}
          onValueChange={onValueChange}
          {...restProps}
          onManualChange={setManual}
        />
      </ErrorBoundary>
    );
  },
);

BasePlaceAutocomplete.displayName = 'BasePlaceAutocomplete';
PlaceAutocomplete.displayName = 'PlaceAutocomplete';
